import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Upload } from 'lucide-react';
import {
  ACCEPTED_IMAGE_MIME_LIST,
  MAX_PRODUCT_IMAGES,
  useTranslation,
  validateProductImageFile,
} from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { cn } from '@/lib/utils';
import { MultiImageUploadSlotView } from './multi-image-upload-slot';
import type { MultiImageUploadProps, MultiImageUploadSlot } from './multi-image-upload-types';

export type { MultiImageUploadProps, MultiImageUploadSlot } from './multi-image-upload-types';

/**
 * Controlled multi-image uploader used by the product form (F-003).
 *
 * Behavior summary (design §9.3):
 *  - "Add image" opens a file picker; multi-select OK up to maxCount.
 *  - Each picked file is client-validated via `validateProductImageFile`.
 *    Invalid files surface a local `imageTooLarge` / `imageInvalidType`
 *    / `tooManyImages` translation key via `errorKey` state.
 *  - Desktop drag reorder via @dnd-kit with a centered grip handle;
 *    mobile gets explicit up/down buttons (and @dnd-kit's
 *    KeyboardSensor keeps Arrow-key reorder available for keyboard
 *    users on every breakpoint).
 *  - Object URLs for `kind: 'new'` slots are created / revoked here —
 *    parents pass raw `File` objects; we own the preview lifecycle.
 *
 * The component is stateless wrt upload I/O — it just reports what
 * the user picked. The parent (ProductForm) kicks off the actual
 * `useCreateProduct` mutation and can flip each slot's `status` to
 * `'uploading'` / `'failed'` while the mutation runs.
 */
export function MultiImageUpload({
  value,
  onChange,
  maxCount = MAX_PRODUCT_IMAGES,
  disabled = false,
  errorMessage,
  errorKey,
  onRetry,
  testIdPrefix = 'product-image-upload',
}: MultiImageUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localErrorKey, setLocalErrorKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // --- Object-URL lifecycle for `kind: 'new'` slots ----------------
  // The parent passes raw Files; we allocate a preview URL for each
  // once and revoke it when the slot leaves `value`. This prevents
  // the "thumbnail flashes" issue you'd get if each render rebuilt
  // the object URL.
  const createdUrlsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const stillPresent = new Set(value.filter((s) => s.kind === 'new').map((s) => s.key));
    // Revoke any URLs whose slot has been removed.
    createdUrlsRef.current.forEach((url, key) => {
      if (!stillPresent.has(key)) {
        URL.revokeObjectURL(url);
        createdUrlsRef.current.delete(key);
      }
    });
  }, [value]);

  useEffect(() => {
    // Cleanup on unmount.
    const urls = createdUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const canAddMore = value.length < maxCount && !disabled;

  // --- File picker handler ----------------------------------------
  const handleFilesSelected = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setLocalErrorKey(null);

      const next: MultiImageUploadSlot[] = [...value];
      const remaining = maxCount - next.length;
      if (files.length > remaining) {
        setLocalErrorKey('admin.products.errors.tooManyImages');
      }

      const picked = Array.from(files).slice(0, Math.max(0, remaining));
      for (const file of picked) {
        const verdict = validateProductImageFile(file);
        if (verdict === 'imageTooLarge') {
          setLocalErrorKey('admin.products.errors.imageTooLarge');
          continue;
        }
        if (verdict === 'imageInvalidType') {
          setLocalErrorKey('admin.products.errors.imageInvalidType');
          continue;
        }
        const key = `new-${crypto.randomUUID()}`;
        const previewUrl = URL.createObjectURL(file);
        createdUrlsRef.current.set(key, previewUrl);
        next.push({
          key,
          kind: 'new',
          previewUrl,
          status: 'idle',
          file,
        });
      }

      onChange(next);
      // Reset the native input so re-selecting the same filename works.
      if (inputRef.current) inputRef.current.value = '';
    },
    [value, maxCount, onChange],
  );

  const handleAddClick = useCallback(() => {
    if (!canAddMore) return;
    setLocalErrorKey(null);
    inputRef.current?.click();
  }, [canAddMore]);

  // --- Remove / reorder callbacks ---------------------------------
  const handleRemove = useCallback(
    (index: number) => {
      setLocalErrorKey(null);
      const next = value.filter((_, i) => i !== index);
      onChange(next);
    },
    [value, onChange],
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index <= 0) return;
      onChange(arrayMove(value, index, index - 1));
    },
    [value, onChange],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= value.length - 1) return;
      onChange(arrayMove(value, index, index + 1));
    },
    [value, onChange],
  );

  // @dnd-kit fires onDragEnd from PointerSensor / KeyboardSensor internals
  // that jsdom can't simulate reliably. The reorder behavior is covered
  // end-to-end by the F-003 Playwright reorder spec (AC7/X1), and the
  // underlying array-move math is exercised directly by the move-up /
  // move-down button tests above.
  /* v8 ignore start */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = value.findIndex((s) => s.key === active.id);
      const newIndex = value.findIndex((s) => s.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onChange(arrayMove(value, oldIndex, newIndex));
    },
    [value, onChange],
  );
  /* v8 ignore stop */

  const slotKeys = useMemo(() => value.map((s) => s.key), [value]);

  // Resolve an error message to display beneath the grid. Parent
  // error (from Zod / submit) wins over local "couldn't add this
  // file" messages.
  const displayError =
    errorMessage ??
    (errorKey ? t(errorKey as TranslationKey) : null) ??
    (localErrorKey ? t(localErrorKey as TranslationKey) : null);

  return (
    <div data-testid={testIdPrefix}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={slotKeys} strategy={rectSortingStrategy}>
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
            data-testid={`${testIdPrefix}-grid`}
          >
            {value.map((slot, index) => (
              <SortableSlot
                key={slot.key}
                slot={slot}
                index={index}
                canMoveUp={index > 0}
                canMoveDown={index < value.length - 1}
                disabled={disabled}
                onRemove={handleRemove}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                onRetry={onRetry}
                testIdPrefix={`${testIdPrefix}-slot`}
              />
            ))}

            {canAddMore ? (
              <button
                type="button"
                onClick={handleAddClick}
                disabled={disabled}
                className={cn(
                  'border-sand-300 hover:border-zaatar hover:bg-zaatar-wash rounded-card text-sand-400 hover:text-zaatar',
                  'flex aspect-square flex-col items-center justify-center gap-1.5 border-[1.5px] border-dashed transition-colors',
                  'focus-visible:ring-zaatar focus-visible:outline-none focus-visible:ring-2',
                )}
                data-testid={`${testIdPrefix}-trigger`}
                aria-label={t('admin.products.form.imageAdd')}
              >
                <Upload size={22} strokeWidth={1.5} aria-hidden />
                <span className="text-xs">{t('admin.products.form.imageAdd')}</span>
              </button>
            ) : null}
          </div>
        </SortableContext>
      </DndContext>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_MIME_LIST}
        multiple
        onChange={(e) => handleFilesSelected(e.target.files)}
        className="sr-only"
        data-testid={`${testIdPrefix}-input`}
        tabIndex={-1}
      />

      {displayError ? (
        <p
          role="alert"
          className="text-terracotta mt-2 text-start text-sm"
          data-testid={`${testIdPrefix}-error`}
        >
          {displayError}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Thin wrapper that wires a single slot to @dnd-kit's `useSortable`.
 * Kept private to this file so the slot component itself stays
 * dnd-kit-free and trivially testable in isolation.
 */
interface SortableSlotProps {
  slot: MultiImageUploadSlot;
  index: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled: boolean;
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRetry?: (index: number) => void;
  testIdPrefix: string;
}

function SortableSlot({
  slot,
  index,
  canMoveUp,
  canMoveDown,
  disabled,
  onRemove,
  onMoveUp,
  onMoveDown,
  onRetry,
  testIdPrefix,
}: SortableSlotProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: slot.key,
    disabled: disabled || slot.status === 'uploading',
  });

  // Forward listeners/attributes as a loosely-typed bag so the view
  // component can stay dnd-kit-free. The spread works because
  // useSortable returns primitive DOM handlers.
  return (
    <MultiImageUploadSlotView
      ref={setNodeRef}
      slot={slot}
      index={index}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      disabled={disabled}
      onRemove={onRemove}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onRetry={onRetry}
      testIdPrefix={testIdPrefix}
      // @dnd-kit's SyntheticListenerMap and DraggableAttributes are opaque
      // internal types that intentionally don't export a stable public shape.
      // The double-cast is the recommended community pattern (see
      // https://github.com/clauderic/dnd-kit/discussions/909). The slot-view
      // component only spreads these onto a DOM element, so the loose typing
      // is safe in practice.
      dragAttributes={attributes as unknown as Record<string, unknown>}
      dragListeners={listeners as unknown as Record<string, (event: unknown) => void>}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      isDragging={isDragging}
    />
  );
}
